import {
  TamaguiProvider as Provider,
  createFont,
  createTamagui,
} from 'tamagui';
import { createMedia } from '@tamagui/react-native-media-driver';
import { config } from '@tamagui/config/v3';
import { createAnimations } from '@tamagui/animations-css';
import { useDebounce } from '@tamagui/use-debounce';

export const debounce = useDebounce;

export const media = createMedia({
  smMobile: { minWidth: 320 },
  mobile: { minWidth: 480 },
  tablet: { minWidth: 768 },
  desktop: { minWidth: 1200 },
  largeDesktop: { minWidth: 1600 },
  isMobile: { maxWidth: 768 },
});

const interFont = createFont({
  family: 'Inter',
  size: {
    1: 10,
    2: 14,
    3: 16,
    4: 20,
    'button-small': 14,
    'button-large': 16,
  },
  lineHeight: {
    1: 12,
    2: 20,
    3: 22.75,
    4: 24,
  },
  weight: {
    1: 400,
    2: 400,
    3: 400,
    4: 400,
  },
  face: {
    400: { normal: 'Inter-Regular' },
    500: { normal: 'Inter-Medium' },
    600: { normal: 'Inter-SemiBold' },
    700: { normal: 'Inter-Bold' },
  },
});

const urbanistFont = createFont({
  family: 'Urbanist',
  size: {
    1: 12,
    2: 14,
    3: 16,
    4: 18,
    'button-small': 14,
    'button-large': 16,
  },
  lineHeight: {
    1: 18,
    2: 21,
    3: 24,
    4: 27,
  },
  weight: {
    1: 400,
    2: 400,
    3: 400,
    4: 400,
  },
  face: {
    400: { normal: 'Urbanist-Regular' },
    500: { normal: 'Urbanist-Medium' },
    600: { normal: 'Urbanist-SemiBold' },
    700: { normal: 'Urbanist-Bold' },
  },
});

const appConfig = createTamagui({
  ...config,
  // TODO: Add tamagui config animation with custom animations
  animations: createAnimations({
    '200ms': 'ease-in 200ms',
    easeout250: 'ease-out 250ms',
    menus300: 'ease-in-out 300ms',
    fade2s: {
      damping: 20,
      mass: 1.2,
      stiffness: 250,
    },
  }),
  media,
  fonts: {
    body: interFont,
    heading: interFont,
  },
  defaultFont: 'body',
});

const hubAppConfig = createTamagui({
  ...config,
  themes: {
    dark: {
      background: '#000',
      color: '#fff',
      headerBgColor: '#212121',
      textColor: '#fff',
      linkColor: '#36B2E1',
      gray1: '#212121',
    },
    light: {
      background: '#fff',
      color: '#000',
      headerBgColor: '#fff',
      textColor: '#fff',
      linkColor: '#36B2E1',
    },
  },
  // TODO: Add tamagui config animation with custom animations
  animations: createAnimations({
    '200ms': 'ease-in 200ms',
    easeout250: 'ease-out 250ms',
    menus300: 'ease-in-out 300ms',
    fade2s: {
      damping: 20,
      mass: 1.2,
      stiffness: 250,
    },
  }),
  media,
  fonts: {
    body: interFont,
    heading: interFont,
  },
  defaultFont: 'body',
});

export type AppConfig = typeof appConfig;

declare module 'tamagui' {
  // overrides TamaguiCustomConfig so your custom types
  // work everywhere you import `tamagui`
  interface TamaguiCustomConfig extends AppConfig {}
}

type Props = {
  children: React.ReactNode;
};

export const TamaguiProvider = (props: Props) => {
  return <Provider config={appConfig}>{props.children}</Provider>;
};

type HubProps = {
  children: React.ReactNode;
  theme: string;
};
export const TamaguiHubProvider = (props: HubProps) => {
  return (
    <Provider config={hubAppConfig} defaultTheme={props.theme}>
      {props.children}
    </Provider>
  );
};

export type {
  AnchorProps,
  ButtonProps,
  CheckboxProps,
  GetProps,
  InputProps,
  RadioGroupItemProps,
  RadioGroupProps,
  ScrollViewProps,
  TamaguiElement,
  TextProps,
  VariantSpreadFunction,
  ViewProps,
  TabsContentProps,
  TabLayout,
  TabsTabProps,
  StackProps,
  SizeTokens,
} from 'tamagui';

export {
  Adapt,
  Anchor,
  AnimatePresence,
  Button,
  Card,
  Checkbox,
  Dialog,
  Group,
  H1,
  H2,
  H3,
  H4,
  H5,
  Image,
  Input,
  isWeb,
  Label,
  ListItem,
  Nav,
  Paragraph,
  Popover,
  Progress,
  Tooltip,
  RadioGroup,
  ScrollView,
  Select,
  Separator,
  Sheet,
  Slider,
  Spinner,
  Stack,
  styled,
  Text,
  TextArea,
  Unspaced,
  View,
  withStaticProperties,
  XStack,
  YGroup,
  YStack,
  Form,
  Theme as TamaguiTheme,
  Tabs,
  useMedia,
  useTheme,
  XGroup,
  ThemeableStack,
  createStyledContext,
  getTokenValue,
  useWindowDimensions,
} from 'tamagui';
